import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ListboxModule } from 'primeng/listbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import { Observable, of } from 'rxjs';
import { PageBaseComponent } from '../../abstracts/page-base/page-base.component';
import { EnumDto } from '../../models/enum-dto';
import { UserOrganization } from '../../models/organization';
import { UserTeam } from '../../models/team';
import { User } from '../../models/user';
import { ApiService } from '../../services/api.service';
import { Parameters } from '../../utilities/parameters';
import { InputNumberModule } from 'primeng/inputnumber';

@Component({
  selector: 'app-users',
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    DialogModule,
    TabsModule,
    ListboxModule,
    InputNumberModule
  ],
  templateUrl: './users.component.html',
  styles: ``,
  providers: [ConfirmationService, MessageService]
})
export class UsersComponent extends PageBaseComponent<User> {
  users: User[] = [];

  roles: EnumDto[] = Parameters.roles;
  selectedRole: EnumDto = {
    id: 0,
    description: ''
  }

  teams: UserTeam[] = [];
  selectedTeams: UserTeam[] = [];
  showTeam: boolean = false;

  organizations: UserOrganization[] = [];
  selectedOrganizations: UserOrganization[] = [];
  selectedOrganization: number = 0;
  showOrganization: boolean = false;

  expandedRows = {};

  isSuperAdmin: boolean = false;

  constructor(apiService: ApiService, messageService: MessageService,
    private confirmationService: ConfirmationService) {
    super(apiService, messageService);

    this.isSuperAdmin = Parameters.loggedUser.role === 1;
  }

  onAddOrgs(): void {
    const allowedIds: number[] = this.selectedItem.organizations.map(o => o.organizationId);
    this.apiService.getOrganizationsSelectable().subscribe({
      next: (o) => {
        this.organizations = o;
        this.selectedOrganizations = o.filter(o => allowedIds.includes(o.organizationId));

        this.showOrganization = true;
      },
      error: (err: HttpErrorResponse) => {
        this.selectedOrganizations = [];
        this.showError(err);
      }
    });
  }

  onAddTeam(organization: UserOrganization): void {
    this.selectedOrganization = organization.organizationId;
    this.selectedTeams = organization.teams;
    this.apiService.getOrganizationTeams(organization.organizationId).subscribe({
      next: (t) => {
        this.teams = t;
        this.showTeam = true;
      },
      error: (err: HttpErrorResponse) => {
        this.selectedOrganization = 0;
        this.selectedTeams = [];
        this.showError(err);
      }
    });
  }

  onDelete(event: Event, user: User): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Vuoi disabilitare l\'utente ${user.userName}?`,
      header: 'Conferma',
      closable: false,
      closeOnEscape: false,
      icon: 'far fa-trash-can',
      rejectButtonProps: {
        label: 'Annulla',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Disabilita',
      },
      accept: () => {
        this.apiService.deleteUser(user).subscribe({
          next: () => {
            this.showSuccess("L'utente è stato disabilitato con successo")
          },
          error: (err: HttpErrorResponse) => {
            this.showError(err);
          }
        });
      },
      reject: () => {
        this.showErrorMessage("La procedura è stata annullata dall'utente!")
      },
    });
  }

  onResetPassword(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Vuoi fare il Reset della Password??',
      header: 'Conferma',
      closable: false,
      closeOnEscape: false,
      icon: 'fas fa-triangle-exclamation',
      rejectButtonProps: {
        label: 'Annulla',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Reset',
      },
      accept: () => {
        this.apiService.resetPassword(this.selectedItem).subscribe({
          next: () => {
            this.showSuccess("La procedura di Reset è stata completata con successo")
          },
          error: (err: HttpErrorResponse) => {
            this.showError(err);
          }
        });
      },
      reject: () => {
        this.showErrorMessage("La procedura è stata annullata dall'utente!")
      },
    });
  }

  override onSave(): void {
    this.selectedItem.role = this.selectedRole.id;

    if (this.isNew) {
      this.apiService.addUser(this.selectedItem).subscribe({
        next: (id) => {
          if (id === 0) {
            this.showDialog = false;
            this.showSuccess("L'utente è stato salvato correttamente");
            this.resetItems();
          } else {
            this.selectedItem.userId = id
            this.updateUserOrganizations();
          }
        },
        error: (err: HttpErrorResponse) => {
          this.showError(err);
        },
        complete: () => {
          this.loadData();
        }
      });
    } else {
      this.apiService.updateUser(this.selectedItem).subscribe({
        next: (r) => {
          if (r) {
            this.showDialog = false;
            this.showSuccess("L'utente è stato aggiornato correttamente");
            this.resetItems();
          } else {
            this.showErrorMessage("Errore nel salvataggio dell'utente");
          }
        },
        error: (err: HttpErrorResponse) => {
          this.showError(err);
        },
        complete: () => {
          this.loadData();
        }
      });
    }
  }

  onUpdateOrganizations(): void {
    const currentIds: number[] = this.selectedItem.organizations.map(o => o.organizationId);
    const selectedIds: number[] = this.selectedOrganizations.map(o => o.organizationId);
    const deletedIds: number[] = currentIds.filter(o => !selectedIds.includes(o));
    const newIds: number[] = selectedIds.filter(o => !currentIds.includes(o));

    this.selectedItem.organizations = this.selectedItem.organizations.filter(o => !deletedIds.includes(o.organizationId));

    this.selectedItem.organizations.push(...this.selectedOrganizations.filter(o => newIds.includes(o.organizationId)));

    this.selectedOrganizations = [];

    this.showOrganization = false;
  }

  onUpdateTeams(): void {
    let org = this.selectedItem.organizations.find(o => o.organizationId === this.selectedOrganization);
    if (org != null)
      org.teams = this.selectedTeams;

    this.selectedOrganization = 0;
    this.selectedTeams = [];

    this.showTeam = false;
  }

  protected override loadData(): void {
    this.loading = true;

    this.apiService.getUsers().subscribe({
      next: (data) => {

        this.users = data;
        this.dtTrigger.next(true);
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.showError(err);
      },
      complete: () => {
        this.loading = false;
      }
    })
  }

  protected override loadSelectedInfo(): Observable<boolean> {
    this.isNew = this.selectedItem.userId === 0;

    if (!this.isNew) {
      const role = this.roles.find(r => r.id === this.selectedItem.role);
      if (role !== undefined) this.selectedRole = role;

      this.apiService.getUserRelations(this.selectedItem.userId).subscribe({
        next: (r) => {
          this.selectedItem.organizations = r;
        },
        error: (err: HttpErrorResponse) => {
          throw err;
        }
      });
    } else {
      this.selectedItem.organizations = [...Parameters.loggedUser.organizations];
    }

    return of(true);
  }

  protected override resetItems(): void {
    this.newItem = {
      userId: 0,
      name: '',
      familyName: '',
      userName: '',
      isActive: true,
      mass: 1.0,
      updatedAt: new Date(),
      role: 0,
      organizations: []
    };
    this.selectedItem = {
      userId: 0,
      name: '',
      familyName: '',
      userName: '',
      isActive: true,
      mass: 1.0,
      updatedAt: new Date(),
      role: 0,
      organizations: []
    };

  }

  private updateUserOrganizations() {
    this.confirmationService.confirm({
      message: `L\'utente ${this.selectedItem.userName} esiste ed è associato ad altre organizzazioni.\nVuoi associarlo alla tua organizzazione?`,
      header: 'Utente Esistente',
      closable: false,
      closeOnEscape: false,
      icon: 'fas fa-user-pen',
      rejectButtonProps: {
        label: 'No',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Associa',
      },
      accept: () => {
        this.apiService.linkUser(this.selectedItem).subscribe({
          next: () => {
            this.showSuccess("L'utente è stato associato con successo")
          },
          error: (err: HttpErrorResponse) => {
            this.showError(err);
          }
        });
      },
      reject: () => {
        this.showErrorMessage("La procedura è stata annullata dall'utente!")
      },
    });
  }
}
